import { useEffect, useState } from "react";
import { Button, Card, Form, Spinner, Table } from "react-bootstrap";
import {
  createAuthUser,
  deleteAuthUser,
  listAuthUsers,
  updateAuthUser,
} from "../../tools/services/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [edits, setEdits] = useState({});

  async function fetchUsers() {
    setLoading(true);
    setError("");
    try {
      const response = await listAuthUsers();
      if (response instanceof Error) throw response;
      setUsers(response.data || []);
    } catch (err) {
      setError(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  function handleEditChange(email, field, value) {
    setEdits((prev) => ({
      ...prev,
      [email]: {
        ...(prev[email] || {}),
        [field]: value,
      },
    }));
  }

  async function handleCreate(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await createAuthUser(newUser);
      if (response instanceof Error) throw response;
      setNewUser({ name: "", email: "", password: "" });
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(user) {
    const update = edits[user.email] || {};
    const hasNameChange =
      update.name !== undefined && update.name !== user.name;
    const hasPasswordChange = Boolean(update.password);
    if (!hasNameChange && !hasPasswordChange) return;
    setSaving(true);
    setError("");
    try {
      const response = await updateAuthUser(user.email, {
        name: hasNameChange ? update.name : undefined,
        password: hasPasswordChange ? update.password : undefined,
      });
      if (response instanceof Error) throw response;
      setEdits((prev) => ({ ...prev, [user.email]: {} }));
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(user) {
    const confirmDelete = window.confirm(
      `Remove access for ${user.email}?`
    );
    if (!confirmDelete) return;
    setSaving(true);
    setError("");
    try {
      const response = await deleteAuthUser(user.email);
      if (response instanceof Error) throw response;
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to delete user");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4">
      <div className="w-full h-20 text-center content-center">
        <span className="w-full text-black fw-bold text-2xl tracking-widest uppercase">
          Admin Users
        </span>
      </div>
      <Card className="p-4 mb-4">
        <Card.Title>Add User</Card.Title>
        <Form onSubmit={handleCreate} className="d-grid gap-3">
          <Form.Group className="text-start" controlId="userName">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={newUser.name}
              onChange={(event) =>
                setNewUser((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="User name"
            />
          </Form.Group>
          <Form.Group className="text-start" controlId="userEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={newUser.email}
              onChange={(event) =>
                setNewUser((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="user@company.com"
              required
            />
          </Form.Group>
          <Form.Group className="text-start" controlId="userPassword">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              value={newUser.password}
              onChange={(event) =>
                setNewUser((prev) => ({
                  ...prev,
                  password: event.target.value,
                }))
              }
              placeholder="********"
              required
            />
          </Form.Group>
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Create User"}
          </Button>
          {error && <div className="text-danger">{error}</div>}
        </Form>
      </Card>

      <Card className="p-4">
        <Card.Title>Users</Card.Title>
        {loading ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : (
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>New Password</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const edit = edits[user.email] || {};
                return (
                  <tr key={user.email}>
                    <td>{user.email}</td>
                    <td>
                      <Form.Control
                        type="text"
                        value={edit.name ?? user.name ?? ""}
                        onChange={(event) =>
                          handleEditChange(
                            user.email,
                            "name",
                            event.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="password"
                        value={edit.password ?? ""}
                        onChange={(event) =>
                          handleEditChange(
                            user.email,
                            "password",
                            event.target.value
                          )
                        }
                        placeholder="********"
                      />
                    </td>
                    <td className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={saving}
                        onClick={() => handleUpdate(user)}
                      >
                        Update
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={saving}
                        onClick={() => handleDelete(user)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
